import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CloseIcon from "@mui/icons-material/Close";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import type { RuleGroupType } from "react-querybuilder";
import {
  pageSectionSubtitleSx,
  pageSectionTitleSx,
} from "../../../constants/pageSectionStyles";
import arrowLeftIcon from "../../../assets/icon-left.svg";
import { brandContainedButtonSx } from "../../../constants/brandContainedButtonStyles";
import { useBrandThemeColors } from "../../../branding";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useAcTenantId } from "../../../hooks/useAcTenantId";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import { useHasPrivilegeStatus } from "../../../hooks/useHasPrivilege";
import {
  resolvePlanSubTypeDisplayName,
  resolvePlanTypeDisplayName,
} from "../../../services/autoTaggingService";
import { AUTO_TAGGING_PRIVILEGES } from "../../../services/permissionsService";
import { useRegulationsNextShellIntegration } from "../../shared/useRegulationsNextShellIntegration";
import ChangeObjectScopeDialog from "../EnhancedTaggingRule/ChangeObjectScopeDialog";
import { mapConditionPayloadToEditorQuery } from "../EnhancedTaggingRule/buildConditionalPlanPayload";
import DiscardEnhancedTaggingRuleChangesDialog from "../EnhancedTaggingRule/DiscardEnhancedTaggingRuleChangesDialog";
import EnhancedTaggingRuleConditionBuilder, {
  createEmptyConditionQuery,
} from "../EnhancedTaggingRule/EnhancedTaggingRuleConditionBuilder";
import EnhancedTaggingRuleObjectStep, {
  createEmptyObjectSelection,
  isObjectSelectionComplete,
  type EnhancedTaggingRuleObjectSelection,
} from "../EnhancedTaggingRule/EnhancedTaggingRuleObjectStep";
import { formatConditionSidebarSummary } from "../EnhancedTaggingRule/formatConditionNaturalLanguage";
import {
  hasEnhancedTaggingConditionData,
  validateEnhancedTaggingConditionQuery,
} from "../EnhancedTaggingRule/validateEnhancedTaggingCondition";
import { buildAiPlanPath } from "../constants";
import TriageRuleFlowSidebar from "./TriageRuleFlowSidebar";
import TriageRuleThenStep, {
  createEmptyFlowSelection,
  isFlowSelectionComplete,
} from "./TriageRuleThenStep";
import type { TriageFlowSelection, TriagePlanRow, TriageRuleWizardStep } from "./types";

const STEP_ORDER: TriageRuleWizardStep[] = ["when", "object", "if", "then"];

const STEP_PANEL_META: Record<
  TriageRuleWizardStep,
  {
    title: string;
    iconBg: string;
    iconColor: string;
    Icon: typeof BookmarkBorderOutlinedIcon;
  }
> = {
  when: {
    title: "When: Auto-triaging runs",
    iconBg: "#E8F8EF",
    iconColor: "#22C55E",
    Icon: BookmarkBorderOutlinedIcon,
  },
  object: {
    title: "If: Object meets the criteria",
    iconBg: "#FFF7E6",
    iconColor: "#FAAD14",
    Icon: TuneOutlinedIcon,
  },
  if: {
    title: "If: Meet the condition(s)",
    iconBg: "#FFF7E6",
    iconColor: "#FAAD14",
    Icon: FilterAltOutlinedIcon,
  },
  then: {
    title: "Then: Run this flow",
    iconBg: "#EEF3FF",
    iconColor: "#295EF1",
    Icon: BoltOutlinedIcon,
  },
};

function buildDraftFingerprint(
  objectSelection: EnhancedTaggingRuleObjectSelection,
  conditionQuery: RuleGroupType,
  flow: TriageFlowSelection,
): string {
  return JSON.stringify({
    objectType: objectSelection.objectType,
    objectSubType: objectSelection.objectSubType,
    condition: conditionQuery,
    flowId: flow.flowId,
    flowName: flow.flowName,
    definitionId: flow.definitionId,
  });
}

function mapRowToFlowSelection(row: TriagePlanRow): TriageFlowSelection {
  return {
    flowId: row.processFlow,
    flowName: row.processFlowName,
    definitionId: row.definitionId,
  };
}

export type EditTriageRuleLocationState = {
  rule: TriagePlanRow;
};

function readEditRuleFromLocation(
  ruleId: string,
  state: unknown,
): TriagePlanRow | null {
  if (!ruleId) return null;
  const rule = (state as EditTriageRuleLocationState | null)?.rule;
  if (!rule || rule.id !== ruleId) return null;
  return rule;
}

export default function AddTriageRulePage() {
  const acTenantId = useAcTenantId();
  const { ruleId = "" } = useParams<{ ruleId?: string }>();
  return (
    <AddTriageRulePageContent
      key={`${acTenantId || "no-ac-tenant"}:${ruleId || "new"}`}
      ruleId={ruleId.trim()}
    />
  );
}

function AddTriageRulePageContent({
  ruleId,
}: Readonly<{ ruleId: string }>) {
  useRegulationsNextShellIntegration();
  const navigate = useNavigate();
  const location = useLocation();
  const { primaryColor } = useBrandThemeColors();
  const { showMessage } = useSnackbar();
  const { planTypes, getObjectSubTypeOptions } =
    useAssessmentPlanFilterOptions();
  const { has: canManageAutoTagging, loaded: privilegesLoaded } =
    useHasPrivilegeStatus(AUTO_TAGGING_PRIVILEGES.manage);
  const isEditMode = Boolean(ruleId);
  const editRule = readEditRuleFromLocation(ruleId, location.state);
  const loadError =
    isEditMode && !editRule
      ? "Rule data is unavailable. Please open edit from the Triage Plan list."
      : null;
  const pageTitle = isEditMode ? "Edit Triage Rule" : "Add Triage Rule";

  const [activeStep, setActiveStep] = useState<TriageRuleWizardStep>(() =>
    isEditMode ? "object" : "when",
  );
  const [objectSelection, setObjectSelection] =
    useState<EnhancedTaggingRuleObjectSelection>(() =>
      editRule
        ? {
            objectType: editRule.objectType,
            objectSubType: editRule.objectSubType,
          }
        : createEmptyObjectSelection(),
    );
  const [showObjectValidation, setShowObjectValidation] = useState(false);
  const [conditionQuery, setConditionQuery] = useState<RuleGroupType>(() =>
    editRule
      ? mapConditionPayloadToEditorQuery(editRule.condition)
      : createEmptyConditionQuery(),
  );
  const [showConditionValidation, setShowConditionValidation] = useState(false);
  const [flowSelection, setFlowSelection] = useState<TriageFlowSelection>(() =>
    editRule ? mapRowToFlowSelection(editRule) : createEmptyFlowSelection(),
  );
  const [showFlowValidation, setShowFlowValidation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [changeScopeConfirmOpen, setChangeScopeConfirmOpen] = useState(false);
  const [pendingObjectSelection, setPendingObjectSelection] =
    useState<EnhancedTaggingRuleObjectSelection | null>(null);
  const [initialDraftFingerprint] = useState(() =>
    buildDraftFingerprint(
      editRule
        ? {
            objectType: editRule.objectType,
            objectSubType: editRule.objectSubType,
          }
        : createEmptyObjectSelection(),
      editRule
        ? mapConditionPayloadToEditorQuery(editRule.condition)
        : createEmptyConditionQuery(),
      editRule ? mapRowToFlowSelection(editRule) : createEmptyFlowSelection(),
    ),
  );

  const isDirty =
    buildDraftFingerprint(objectSelection, conditionQuery, flowSelection) !==
    initialDraftFingerprint;

  const stepIndex = STEP_ORDER.indexOf(activeStep);
  const isFirstStep = stepIndex <= 0;
  const isLastStep = activeStep === "then";
  const panelMeta = STEP_PANEL_META[activeStep];
  const PanelIcon = panelMeta.Icon;

  const objectRequiresSubType =
    Boolean(objectSelection.objectType) &&
    getObjectSubTypeOptions(objectSelection.objectType).length > 0;

  const objectStepComplete = isObjectSelectionComplete(
    objectSelection,
    objectRequiresSubType,
  );

  const conditionStepComplete =
    validateEnhancedTaggingConditionQuery(conditionQuery).valid;
  const flowStepComplete = isFlowSelectionComplete(flowSelection);

  const disabledSteps = useMemo(
    () =>
      objectStepComplete
        ? ([] as const)
        : (["if", "then"] as const satisfies readonly TriageRuleWizardStep[]),
    [objectStepComplete],
  );

  const stepSubtitles = useMemo(() => {
    const objectTypeName = resolvePlanTypeDisplayName(
      planTypes,
      objectSelection.objectType,
    );
    const objectSubTypeName = resolvePlanSubTypeDisplayName(
      planTypes,
      objectSelection.objectType,
      objectSelection.objectSubType,
    );
    const ifSummary = formatConditionSidebarSummary(conditionQuery);
    return {
      object: objectStepComplete
        ? objectSubTypeName
          ? `Object = ${objectTypeName}, Object Sub-Type = ${objectSubTypeName}`
          : `Object = ${objectTypeName}`
        : "Please select object type and sub-type",
      if: ifSummary || "Please add condition(s)",
      then: flowStepComplete
        ? `Apply process: ${flowSelection.flowName}`
        : "Please select a process flow",
    } satisfies Partial<Record<TriageRuleWizardStep, string>>;
  }, [
    conditionQuery,
    flowSelection.flowName,
    flowStepComplete,
    objectSelection,
    objectStepComplete,
    planTypes,
  ]);

  const invalidSteps = useMemo(() => {
    const next: TriageRuleWizardStep[] = [];
    if (showObjectValidation && !objectStepComplete) next.push("object");
    if (showConditionValidation && !conditionStepComplete) next.push("if");
    if (showFlowValidation && !flowStepComplete) next.push("then");
    return next;
  }, [
    conditionStepComplete,
    flowStepComplete,
    objectStepComplete,
    showConditionValidation,
    showFlowValidation,
    showObjectValidation,
  ]);

  const leavePage = useCallback(() => {
    navigate(buildAiPlanPath("triagePlan"));
  }, [navigate]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    leavePage();
  }, [isDirty, leavePage]);

  const handleConfirmDiscard = useCallback(() => {
    setDiscardConfirmOpen(false);
    leavePage();
  }, [leavePage]);

  const handleStepChange = useCallback(
    (step: TriageRuleWizardStep) => {
      if (submitting) return;
      if (
        (step === "if" || step === "then") &&
        !isObjectSelectionComplete(objectSelection, objectRequiresSubType)
      ) {
        setShowObjectValidation(true);
        setActiveStep("object");
        showMessage("Please select object type and sub-type.", "warning");
        return;
      }
      setActiveStep(step);
    },
    [objectRequiresSubType, objectSelection, showMessage, submitting],
  );

  const handleObjectSelectionChange = useCallback(
    (next: EnhancedTaggingRuleObjectSelection) => {
      const scopeChanged =
        next.objectType !== objectSelection.objectType ||
        next.objectSubType !== objectSelection.objectSubType;
      if (!scopeChanged) return;

      if (hasEnhancedTaggingConditionData(conditionQuery)) {
        setPendingObjectSelection(next);
        setChangeScopeConfirmOpen(true);
        return;
      }

      setObjectSelection(next);
      setShowObjectValidation(false);
    },
    [conditionQuery, objectSelection.objectSubType, objectSelection.objectType],
  );

  const handleCancelChangeScope = useCallback(() => {
    setChangeScopeConfirmOpen(false);
    setPendingObjectSelection(null);
  }, []);

  const handleConfirmChangeScope = useCallback(() => {
    if (pendingObjectSelection) {
      setObjectSelection(pendingObjectSelection);
      setConditionQuery(createEmptyConditionQuery());
      setShowConditionValidation(false);
      setShowObjectValidation(false);
    }
    setChangeScopeConfirmOpen(false);
    setPendingObjectSelection(null);
  }, [pendingObjectSelection]);

  const handleConditionQueryChange = useCallback((nextQuery: RuleGroupType) => {
    setConditionQuery(nextQuery);
    if (validateEnhancedTaggingConditionQuery(nextQuery).valid) {
      setShowConditionValidation(false);
    }
  }, []);

  const handleFlowChange = useCallback((next: TriageFlowSelection) => {
    setFlowSelection(next);
    if (isFlowSelectionComplete(next)) {
      setShowFlowValidation(false);
    }
  }, []);

  const handleBack = useCallback(() => {
    if (submitting) return;
    if (isFirstStep) {
      handleClose();
      return;
    }
    setActiveStep(STEP_ORDER[stepIndex - 1]);
  }, [handleClose, isFirstStep, stepIndex, submitting]);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;

    if (!isObjectSelectionComplete(objectSelection, objectRequiresSubType)) {
      setShowObjectValidation(true);
      setActiveStep("object");
      showMessage("Please select object type and sub-type.", "warning");
      return;
    }

    const conditionValidation =
      validateEnhancedTaggingConditionQuery(conditionQuery);
    if (!conditionValidation.valid) {
      setShowConditionValidation(true);
      setActiveStep("if");
      if (conditionValidation.message) {
        showMessage(conditionValidation.message, "warning");
      }
      return;
    }

    if (!isFlowSelectionComplete(flowSelection)) {
      setShowFlowValidation(true);
      setActiveStep("then");
      showMessage("Please select a process flow.", "warning");
      return;
    }

    setShowObjectValidation(false);
    setShowConditionValidation(false);
    setShowFlowValidation(false);
    setSubmitting(true);
    try {
      // Create/update API is not wired yet — keep wizard UX ready for integration.
      showMessage(
        isEditMode
          ? "Triage rule update is coming soon."
          : "Triage rule creation is coming soon.",
        "info",
      );
      leavePage();
    } finally {
      setSubmitting(false);
    }
  }, [
    conditionQuery,
    flowSelection,
    isEditMode,
    leavePage,
    objectRequiresSubType,
    objectSelection,
    showMessage,
    submitting,
  ]);

  const handleNext = useCallback(() => {
    if (submitting) return;

    if (activeStep === "object") {
      if (
        !isObjectSelectionComplete(objectSelection, objectRequiresSubType)
      ) {
        setShowObjectValidation(true);
        showMessage("Please select object type and sub-type.", "warning");
        return;
      }
      setShowObjectValidation(false);
    }

    if (activeStep === "if") {
      const conditionValidation =
        validateEnhancedTaggingConditionQuery(conditionQuery);
      if (!conditionValidation.valid) {
        setShowConditionValidation(true);
        if (conditionValidation.message) {
          showMessage(conditionValidation.message, "warning");
        }
        return;
      }
      setShowConditionValidation(false);
    }

    if (activeStep === "then") {
      void handleSubmit();
      return;
    }

    setActiveStep(STEP_ORDER[stepIndex + 1]);
  }, [
    activeStep,
    conditionQuery,
    handleSubmit,
    objectRequiresSubType,
    objectSelection,
    showMessage,
    stepIndex,
    submitting,
  ]);

  const panelBody = useMemo(() => {
    if (activeStep === "when") {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            bgcolor: "#EEF3FF",
            borderRadius: "8px",
            px: 2,
            py: 1.75,
          }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 20, color: "#295EF1", mt: "2px", flexShrink: 0 }}
          />
          <Typography
            sx={{ fontSize: "14px", color: "#262626", lineHeight: 1.6 }}
          >
            This rule is triggered when auto-triaging runs. The system checks
            eligible items against the conditions defined in this rule. If the
            conditions are met, the configured flow will be triggered
            automatically. Condition dimensions and values must be configured in
            the{" "}
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => navigate(buildAiPlanPath("taggingPlan"))}
              sx={{
                color: "#295EF1",
                fontSize: "14px",
                fontWeight: 500,
                verticalAlign: "baseline",
                cursor: "pointer",
              }}
            >
              Classification
            </Link>{" "}
            before they can be selected and used in Triage Rules.
          </Typography>
        </Box>
      );
    }

    if (activeStep === "object") {
      return (
        <EnhancedTaggingRuleObjectStep
          value={objectSelection}
          onChange={handleObjectSelectionChange}
          showValidation={showObjectValidation}
        />
      );
    }

    if (activeStep === "if") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ fontSize: "14px", color: "#595959", lineHeight: 1.6 }}>
            Configure concepts and values in the Triage Plan, then select them
            here to define triage conditions.
          </Typography>
          <EnhancedTaggingRuleConditionBuilder
            query={conditionQuery}
            onQueryChange={handleConditionQueryChange}
            targetType={objectSelection.objectType}
            showValidation={showConditionValidation}
            hideIntro
          />
        </Box>
      );
    }

    return (
      <TriageRuleThenStep
        value={flowSelection}
        onChange={handleFlowChange}
        showValidation={showFlowValidation}
      />
    );
  }, [
    activeStep,
    conditionQuery,
    flowSelection,
    handleConditionQueryChange,
    handleFlowChange,
    handleObjectSelectionChange,
    navigate,
    objectSelection,
    showConditionValidation,
    showFlowValidation,
    showObjectValidation,
  ]);

  if (privilegesLoaded && !canManageAutoTagging) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          px: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Access Denied
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You do not have permission to manage Triage Rules.
        </Typography>
        <Button onClick={leavePage} sx={{ mt: 1 }}>
          Back to Classification AI Plan
        </Button>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          px: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Unable to load rule
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {loadError}
        </Typography>
        <Button onClick={handleClose} sx={{ mt: 1 }}>
          Back to Classification AI Plan
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: "#fff",
      }}
    >
      <Box sx={{ px: 0, pt: 0, pb: 2, borderBottom: "1px solid #F0F0F0" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <IconButton
            aria-label="Go back"
            onClick={handleClose}
            disabled={submitting}
            sx={{
              width: 32,
              height: 32,
              p: 1,
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            <Box
              component="img"
              src={arrowLeftIcon}
              alt=""
              sx={{
                width: 16.25,
                height: 16,
                display: "block",
                objectFit: "contain",
              }}
            />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Breadcrumbs
              separator="›"
              sx={{
                mb: "4px",
                "& .MuiBreadcrumbs-separator": { color: "#9ca3af", mx: 1 },
              }}
            >
              <Link
                component="button"
                variant="body1"
                onClick={handleClose}
                sx={{
                  color: primaryColor,
                  textDecoration: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Classification AI Plan
              </Link>
              <Typography
                sx={{
                  color: "#374151",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                {pageTitle}
              </Typography>
            </Breadcrumbs>
            <Typography sx={{ ...pageSectionTitleSx, mb: "4px" }}>
              {pageTitle}
            </Typography>
            <Typography sx={pageSectionSubtitleSx}>
              Define when a process flow should run after auto-triaging.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            disabled={submitting}
            sx={{ width: 32, height: 32, color: "#8c8c8c" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "stretch",
          gap: 2,
          p: 3,
          overflow: "hidden",
          bgcolor: "#F9FAFB",
        }}
      >
        <TriageRuleFlowSidebar
          activeStep={activeStep}
          onStepChange={handleStepChange}
          stepSubtitles={stepSubtitles}
          disabledSteps={disabledSteps}
          invalidSteps={invalidSteps}
        />

        <Box
          sx={{
            flex: "1 1 50%",
            width: "50%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2.5,
              py: 2,
              borderBottom: "1px solid #F0F0F0",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                bgcolor: panelMeta.iconBg,
                color: panelMeta.iconColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PanelIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography
              sx={{ fontSize: "16px", fontWeight: 600, color: "#262626" }}
            >
              {panelMeta.title}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 2.5 }}>
            {panelBody}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              px: 2.5,
              py: 2,
              borderTop: "1px solid #F0F0F0",
              flexShrink: 0,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={submitting}
              sx={{
                textTransform: "none",
                height: 32,
                borderColor: "#D9D9D9",
                color: "#262626",
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={submitting}
              sx={brandContainedButtonSx}
            >
              {isLastStep
                ? submitting
                  ? isEditMode
                    ? "Saving…"
                    : "Submitting…"
                  : isEditMode
                    ? "Save"
                    : "Submit"
                : "Next"}
            </Button>
          </Box>
        </Box>
      </Box>

      <DiscardEnhancedTaggingRuleChangesDialog
        open={discardConfirmOpen}
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />

      <ChangeObjectScopeDialog
        open={changeScopeConfirmOpen}
        onCancel={handleCancelChangeScope}
        onConfirm={handleConfirmChangeScope}
      />
    </Box>
  );
}
